import React from "react";
import { VELLUM as T, EASE_OUT, draftCss, mono } from "./tokens";

/* The folio, as the live landing page.

   Two tracks - the measure the visitor reads and the margin that annotates
   it - divided by one unbroken rule that the reader's position descends. The
   sections keep the reading order, the behaviour and the words of the page
   this replaces; only the drawing is new.

   The container matches the header and footer exactly (1280 / 20px), so the
   brand mark above sits on the same vertical as the measure's left edge. On
   a drawing, a misaligned edge is the first thing the eye finds. */

export const P = "folio";
export const PAGE_MAX = 1280;
export const PAGE_PAD = 20;
export const MARGIN_W = 296;
export const COL_GAP = 40;

/* The rule inks in as the folio is read. Driven by a native scroll-driven
   animation, so there is no scroll listener and no per-frame React state;
   clients without `animation-timeline` see a plain unfilled rule. */
export const Spine: React.FC = () => (
  <div
    className={`${P}-spine`}
    aria-hidden
    style={{ gridColumn: 2, gridRow: "1 / -1", alignSelf: "stretch", position: "relative", width: 1, background: T.rule }}
  >
    <span
      className={`${P}-spine-fill`}
      style={{
        position: "absolute",
        left: -1.5,
        top: 0,
        width: 4,
        height: "100%",
        background: T.accent,
        transformOrigin: "top center",
        transform: "scaleY(0)",
      }}
    />
  </div>
);

export const Leaf: React.FC<{
  code: string;
  measure: React.ReactNode;
  margin?: React.ReactNode;
  first?: boolean;
}> = ({ code, measure, margin, first }) => {
  const top = first ? 44 : 72;
  return (
    <>
      <section className={`${P}-measure`} style={{ gridColumn: 1, paddingTop: top, paddingBottom: 72 }}>
        {measure}
      </section>
      <aside className={`${P}-margin`} style={{ gridColumn: 3, paddingTop: top, paddingBottom: 72 }}>
        <div className={`${P}-sticky`}>
          <div className={`${P}-margin-inner`}>
            <div style={{ ...mono(T, T.accent), marginBottom: 14 }}>{code}</div>
            {margin}
          </div>
        </div>
      </aside>
    </>
  );
};

/* Emitted once, at the root of the page. */
export const folioCss = `
  ${draftCss(T, P)}

  .${P}-root { scroll-behavior: smooth; }
  .${P}-sticky { position: sticky; top: 84px; }

  /* The theme's own placeholder measures 3.02:1 against the ground and
     fails WCAG AA 1.4.3; mute measures 4.96:1. Emitted after draftCss so
     it wins at equal specificity. */
  .${P}-input::placeholder { color: ${T.mute}; }

  .${P}-sr {
    position: absolute; width: 1px; height: 1px;
    padding: 0; margin: -1px; overflow: hidden;
    clip: rect(0 0 0 0); white-space: nowrap; border: 0;
  }

  /* States. The system has no shadows and no rounded corners, so a hover is
     a change of ink and rule weight, never a lift. */
  .${P}-rulelink:hover { color: ${T.accent}; border-bottom-color: ${T.accent}; }
  .${P}-index-row:hover { border-bottom-color: ${T.ink}; }
  .${P}-index-row:hover > span:first-child { color: ${T.accent}; }
  .${P}-rulelink:focus-visible,
  .${P}-index-row:focus-visible,
  .${P}-search-btn:focus-visible {
    outline: 2px solid ${T.accent}; outline-offset: 3px;
  }
  .${P}-search-btn:hover { background: ${T.accentDeep}; }
  .${P}-searchfield:focus-within { border-color: ${T.accent}; }

  /* The two buttons that sit ON the accent plate need their own hovers: the
     shared rules fill with accent, which on an accent ground erases the
     button. These invert against the plate instead. */
  .${P}-invert:hover { background: ${T.ink}; color: ${T.ground}; }
  .${P}-ghost:hover { background: ${T.onAccent}; color: ${T.accentDeep}; }

  @keyframes ${P}-ink-rule {
    from { transform: scaleY(0); }
    to   { transform: scaleY(1); }
  }
  @supports (animation-timeline: view()) {
    @media (prefers-reduced-motion: no-preference) {
      .${P}-spine-fill {
        animation: ${P}-ink-rule linear both;
        animation-timeline: view(block);
        animation-range: cover 0% cover 100%;
      }
    }
  }

  /* The one authored moment: the dimension lines that bracket the title draw
     outward at load, then the sheet is still. Their resting state is drawn,
     so reduced motion simply starts there. */
  @keyframes ${P}-draw { from { transform: scaleX(0); } to { transform: scaleX(1); } }
  .${P}-dim { display: block; transform-origin: left center; }
  @media (prefers-reduced-motion: no-preference) {
    .${P}-dim { animation: ${P}-draw 720ms ${EASE_OUT} both; }
    .${P}-dim-b { animation-delay: 130ms; }
  }

  /* Each leaf is annotated as it arrives. The timeline is taken from the
     margin column, which scrolls, rather than from the sticky block inside
     it, whose own progress would freeze while it is pinned. */
  .${P}-margin { view-timeline-name: --${P}-leaf; view-timeline-axis: block; }
  @supports (animation-timeline: view()) {
    @media (prefers-reduced-motion: no-preference) {
      @keyframes ${P}-annotate {
        from { opacity: 0; transform: translateX(-16px); }
        to   { opacity: 1; transform: translateX(0); }
      }
      .${P}-margin-inner {
        animation: ${P}-annotate linear both;
        animation-timeline: --${P}-leaf;
        animation-range: cover 0% cover 20%;
      }
    }
  }

  /* The issued-to plate is inked across rather than faded in. */
  @supports (animation-timeline: view()) {
    @media (prefers-reduced-motion: no-preference) {
      @keyframes ${P}-wipe {
        from { clip-path: inset(0 100% 0 0); }
        to   { clip-path: inset(0 0 0 0); }
      }
      .${P}-plate {
        animation: ${P}-wipe linear both;
        animation-timeline: view();
        animation-range: cover 3% cover 32%;
      }
    }
  }

  /* Feedback: an entry steps forward off its own left margin. */
  .${P}-entry { transition: border-color 160ms ease, background 160ms ease, transform 200ms ${EASE_OUT}; transform-origin: left center; }
  .${P}-entry:hover { transform: scale(1.008); }

  @keyframes ${P}-pulse { 0%,100% { opacity: 1 } 50% { opacity: 0.3 } }

  @media (prefers-reduced-motion: reduce) {
    .${P}-root { scroll-behavior: auto; }
    .${P}-entry { transition: none !important; }
    .${P}-entry:hover { transform: none; }
    .${P}-skeleton { animation: none !important; }
  }

  @media (max-width: 980px) {
    .${P}-grid { grid-template-columns: 1px minmax(0, 1fr) !important; column-gap: 0 !important; }
    .${P}-spine { grid-column: 1 !important; }
    .${P}-measure, .${P}-margin { grid-column: 2 !important; padding-left: 22px !important; }
    .${P}-margin { padding-top: 0 !important; }
    .${P}-sticky { position: static !important; }
    .${P}-margin-inner { border-top: 1px solid ${T.rule}; padding-top: 18px; margin-bottom: 52px; }
    .${P}-under { grid-template-columns: 44px minmax(0,1fr) !important; }
    .${P}-under > span:last-child { grid-column: 2; text-align: left !important; }
    .${P}-doors { grid-template-columns: 1fr !important; row-gap: 40px; }
    .${P}-doors-rule { width: auto !important; height: 1px; }
  }
`;
